import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Plus, Edit, Trash2, Save, X, Navigation } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface NavigationItem {
  id?: string;
  title: string;
  path: string;
  icon_name?: string;
  parent_id?: string;
  sort_order: number;
  is_published: boolean;
  requires_auth: boolean;
  requires_subscription: boolean;
}

export default function NavigationManager() {
  const [items, setItems] = useState<NavigationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<NavigationItem | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const defaultItem: NavigationItem = {
    title: '',
    path: '',
    sort_order: 0,
    is_published: true,
    requires_auth: false,
    requires_subscription: false
  };

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const { data, error } = await supabase
        .from('navigation_items')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('Error loading navigation items:', error);
      toast.error('Failed to load navigation items');
    } finally {
      setLoading(false);
    }
  };

  const saveItem = async (item: NavigationItem) => {
    try {
      if (item.id) {
        const { error } = await supabase
          .from('navigation_items')
          .update(item)
          .eq('id', item.id);
        if (error) throw error;
        toast.success('Navigation item updated successfully');
      } else {
        const { error } = await supabase
          .from('navigation_items')
          .insert([item]);
        if (error) throw error;
        toast.success('Navigation item created successfully');
      }
      
      loadItems();
      setEditingItem(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error('Failed to save navigation item');
    }
  };

  const deleteItem = async (id: string) => {
    try {
      const { error } = await supabase
        .from('navigation_items')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Navigation item deleted successfully');
      loadItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete navigation item');
    }
  };

  const ItemForm = ({ item, onSave, onCancel }: { 
    item: NavigationItem; 
    onSave: (item: NavigationItem) => void; 
    onCancel: () => void; 
  }) => {
    const [formData, setFormData] = useState(item);

    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>{item.id ? 'Edit Navigation Item' : 'Create New Navigation Item'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Dashboard"
              />
            </div>
            
            <div>
              <Label htmlFor="path">Path</Label>
              <Input
                id="path"
                value={formData.path}
                onChange={(e) => setFormData({ ...formData, path: e.target.value })}
                placeholder="/dashboard"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="icon_name">Icon Name (Lucide)</Label>
              <Input
                id="icon_name"
                value={formData.icon_name || ''}
                onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
                placeholder="Home"
              />
            </div>
            
            <div>
              <Label htmlFor="sort_order">Sort Order</Label>
              <Input
                id="sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Switch
                id="is_published"
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
              />
              <Label htmlFor="is_published">Published</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="requires_auth"
                checked={formData.requires_auth}
                onCheckedChange={(checked) => setFormData({ ...formData, requires_auth: checked })}
              />
              <Label htmlFor="requires_auth">Requires Authentication</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="requires_subscription"
                checked={formData.requires_subscription}
                onCheckedChange={(checked) => setFormData({ ...formData, requires_subscription: checked })}
              />
              <Label htmlFor="requires_subscription">Requires Subscription</Label>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={() => onSave(formData)}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div>Loading navigation items...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Navigation Management</h2>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add New Item
        </Button>
      </div>
      
      <div className="p-4 bg-muted rounded-lg">
        <h3 className="font-semibold mb-2">🧭 Dynamic Navigation</h3>
        <p className="text-sm text-muted-foreground">
          Control your app's navigation structure dynamically. Add, remove, or reorder menu items instantly. 
          Set access requirements and publish/unpublish items as needed.
        </p>
      </div>
      
      {isCreating && (
        <ItemForm
          item={defaultItem}
          onSave={saveItem}
          onCancel={() => setIsCreating(false)}
        />
      )}
      
      {editingItem && (
        <ItemForm
          item={editingItem}
          onSave={saveItem}
          onCancel={() => setEditingItem(null)}
        />
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => (
          <Card key={item.id} className={!item.is_published ? 'opacity-60' : ''}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Navigation className="h-4 w-4" />
                    {item.title}
                  </CardTitle>
                  <code className="text-xs text-muted-foreground">{item.path}</code>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingItem(item)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => item.id && deleteItem(item.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {item.icon_name && (
                  <div className="text-sm">
                    <strong>Icon:</strong> {item.icon_name}
                  </div>
                )}
                
                <div className="flex flex-wrap gap-1">
                  {item.is_published ? (
                    <Badge variant="default">Published</Badge>
                  ) : (
                    <Badge variant="secondary">Draft</Badge>
                  )}
                  
                  {item.requires_auth && (
                    <Badge variant="outline">Auth Required</Badge>
                  )}
                  
                  {item.requires_subscription && (
                    <Badge variant="outline">Premium</Badge>
                  )}
                </div>
                
                <div className="text-xs text-muted-foreground">
                  Sort Order: {item.sort_order}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}